// Cloudflare Workers Indexer URL
const API_URL =
  import.meta.env.VITE_INDEXER_URL ||
  "https://habitat-helius-indexer.habitatsystemacc.workers.dev";

export interface BalanceChange {
  timestamp: number;
  balanceAfter: string;
}

export interface ClaimProof {
  epochIndex: number;
  userStakeSeconds: string;
  proof: BalanceChange[];
  hadEarlyUnstake: boolean;
  estimatedGrossEp?: string;
  estimatedNetEp?: string;
}

export interface ClaimableEpoch {
  epochIndex: number;
  epochStart: number;
  epochEnd: number;
  userStakeSeconds: string;
  claimableReward: string;
  merkleProof: string[];
  totalStakeSeconds: string;
  epochEp: string;
}

export interface UserStats {
  userPubkey: string;
  currentBalance: string;
  totalStaked: string;
  totalUnstaked: string;
  totalClaimed: string;
  firstStakeAt: string | null;
  lastActivityAt: string | null;
  updatedAt: string | null;
}

export interface GlobalStats {
  totalUsers: number;
  totalStaked: string;
  totalValueLocked: string;
  totalClaimed: string;
}

/**
 * Generate a claim proof for a user and epoch (legacy format)
 */
export async function generateClaimProof(
  userPubkey: string,
  epochIndex: number
): Promise<ClaimProof> {
  const response = await fetch(`${API_URL}/proof/${userPubkey}/${epochIndex}`);

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || "Failed to generate proof");
  }

  return await response.json();
}

/**
 * Get epoch reward with merkle proof for a user
 */
export async function getEpochReward(
  userPubkey: string,
  epochIndex: number
): Promise<{
  epAmount: string;
  stakeSeconds: string;
  merkleProof: string[];
} | null> {
  const response = await fetch(
    `${API_URL}/rewards/${epochIndex}/${userPubkey}`
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error("Failed to fetch epoch reward");
  }

  const data = await response.json();
  return data.success ? data.data : null;
}

/**
 * Get merkle root for a finalized epoch
 */
export async function getEpochMerkleRoot(
  epochIndex: number
): Promise<string | null> {
  const response = await fetch(
    `${API_URL}/api/epoch/${epochIndex}/merkle-root`
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error("Failed to fetch merkle root");
  }

  const data = await response.json();
  return data.merkleRoot;
}

/**
 * Get all claimable epochs for a user
 */
export async function getClaimableEpochs(
  userPubkey: string
): Promise<ClaimableEpoch[]> {
  const response = await fetch(`${API_URL}/api/claimable/${userPubkey}`);

  if (!response.ok) {
    throw new Error("Failed to fetch claimable epochs");
  }

  return await response.json();
}

/**
 * Get user statistics
 */
export async function getUserStats(
  userPubkey: string
): Promise<UserStats | null> {
  const response = await fetch(`${API_URL}/api/user/${userPubkey}`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error("Failed to fetch user stats");
  }

  return await response.json();
}

/**
 * Get global statistics
 */
export async function getGlobalStats(): Promise<GlobalStats> {
  const response = await fetch(`${API_URL}/api/stats`);

  if (!response.ok) {
    throw new Error("Failed to fetch global stats");
  }

  return await response.json();
}

/**
 * Get all epochs
 */
export async function getAllEpochs(): Promise<any[]> {
  const response = await fetch(`${API_URL}/api/epochs`);

  if (!response.ok) {
    throw new Error("Failed to fetch epochs");
  }

  return await response.json();
}

/**
 * Get epoch information
 */
export async function getEpochInfo(epochIndex: number): Promise<any> {
  const response = await fetch(`${API_URL}/api/epoch/${epochIndex}`);

  if (!response.ok) {
    throw new Error("Failed to fetch epoch info");
  }

  return await response.json();
}

/**
 * Get user's stake events
 */
export async function getUserStakes(
  userPubkey: string,
  epochIndex?: number
): Promise<any[]> {
  const url = epochIndex
    ? `${API_URL}/api/user/${userPubkey}/stakes?epochIndex=${epochIndex}`
    : `${API_URL}/api/user/${userPubkey}/stakes`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Failed to fetch user stakes");
  }

  return await response.json();
}

/**
 * Get user's unstake events
 */
export async function getUserUnstakes(
  userPubkey: string,
  epochIndex?: number
): Promise<any[]> {
  const url = epochIndex
    ? `${API_URL}/api/user/${userPubkey}/unstakes?epochIndex=${epochIndex}`
    : `${API_URL}/api/user/${userPubkey}/unstakes`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Failed to fetch user unstakes");
  }

  return await response.json();
}

/**
 * Get user's claim events
 */
export async function getUserClaims(userPubkey: string): Promise<any[]> {
  const response = await fetch(`${API_URL}/api/user/${userPubkey}/claims`);

  if (!response.ok) {
    throw new Error("Failed to fetch user claims");
  }

  return await response.json();
}

/**
 * Get user's balance changes for an epoch
 */
export async function getBalanceChanges(
  userPubkey: string,
  epochIndex: number
): Promise<any[]> {
  const response = await fetch(
    `${API_URL}/api/user/${userPubkey}/balance-changes/${epochIndex}`
  );

  if (!response.ok) {
    throw new Error("Failed to fetch balance changes");
  }

  return await response.json();
}

/**
 * Check indexer health
 */
export async function checkHealth(): Promise<any> {
  const response = await fetch(`${API_URL}/health`);

  if (!response.ok) {
    throw new Error("Indexer is not healthy");
  }

  return await response.json();
}
